import { playCelebrationSound } from '../services/celebrationSound';
import { notifyStreakIncrease } from '../services/notifications';
import { renderIcon } from './icons';

/**
 * Streak kutlaması — tam ekran görsel + ses.
 *
 * NEDEN VAR: §7.8'e göre day streak kazanıldığında özel bir ses
 * çalmalıydı. Faz 13'te iPhone'da ölçtük: bildirime özel ses vermek
 * iOS'ta mümkün değil, titreşim API'si de yok. Bu yüzden kutlama
 * uygulamanın İÇİNDE yapılıyor — kullanıcı seriyi kazandığını
 * kaçırmasın diye ekranı kaplayan, sesli bir kutlama.
 */

/** Kutlama kendiliğinden kapanmadan önce ekranda kalma süresi (ms). */
const AUTO_DISMISS_MS = 6000;

/** Aynı anda iki kutlama açılmasın diye açık olanın referansı. */
let activeOverlay: HTMLElement | null = null;

export interface CelebrationOptions {
  /** Kutlanacak yeni seri değeri. */
  streak: number;
  /** Bu, kullanıcının şimdiye kadarki en uzun serisi mi? */
  isRecord: boolean;
}

/**
 * Arka planda uçuşan kıvılcımların HTML'i.
 *
 * Her kıvılcım farklı bir yatay konum, gecikme ve süre alıyor; hepsi
 * aynı anda aynı şekilde hareket etseydi animasyon mekanik görünürdü.
 * Değerler CSS değişkeni olarak veriliyor, animasyonun kendisi CSS'te.
 */
function renderSparks(): string {
  const sparks = Array.from({ length: 14 }, (_, index) => {
    const left = (index * 7 + 4) % 100;
    const delay = (index % 7) * 0.18;
    const duration = 1.8 + (index % 5) * 0.25;
    const size = 5 + (index % 4) * 3;
    return `<span class="celebration__spark"
              style="--spark-left:${left}%; --spark-delay:${delay}s;
                     --spark-duration:${duration}s; --spark-size:${size}px"></span>`;
  });
  return sparks.join('');
}

/**
 * Kutlamayı gösterir.
 *
 * Ses ve görsel birlikte başlar. Ses çalınamazsa (iOS, dokunuş öncesi)
 * kutlama yine de görünür — `playCelebrationSound` sesi kullanıcının
 * ilk dokunuşuna erteler.
 */
export function showStreakCelebration(options: CelebrationOptions): void {
  // Önceki kutlama hâlâ açıksa onu kapat: üst üste binmiş iki katman
  // hem kötü görünür hem de kapatma dokunuşunu belirsizleştirir.
  activeOverlay?.remove();

  const overlay = document.createElement('div');
  overlay.className = 'celebration';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-live', 'assertive');
  overlay.innerHTML = `
    <div class="celebration__sparks" aria-hidden="true">${renderSparks()}</div>
    <div class="celebration__card">
      <span class="celebration__flame">${renderIcon('flame', { size: 64 })}</span>
      <p class="celebration__count">${options.streak}</p>
      <p class="celebration__unit">günlük seri</p>
      <p class="celebration__note">
        ${options.isRecord ? 'Yeni rekorun! Böyle devam.' : 'Seriyi sürdürdün.'}
      </p>
      <button type="button" class="celebration__dismiss">Devam</button>
    </div>
  `;

  document.body.appendChild(overlay);
  activeOverlay = overlay;
  void playCelebrationSound();

  // Kendiliğinden kapanma. Kullanıcı daha önce kapatırsa bu iptal
  // edilmeli, yoksa sonradan açılan bir kutlamayı kapatırdı.
  const autoDismiss = window.setTimeout(() => close(), AUTO_DISMISS_MS);

  function close(): void {
    window.clearTimeout(autoDismiss);
    overlay.classList.add('celebration--closing');
    // Kapanış animasyonu bitince DOM'dan çıkar.
    window.setTimeout(() => {
      overlay.remove();
      if (activeOverlay === overlay) activeOverlay = null;
    }, 220);
  }

  // Ekranın herhangi bir yerine dokunmak kapatır — kullanıcı küçük bir
  // butonu bulmak zorunda kalmasın.
  overlay.addEventListener('click', () => close());
}

/**
 * Seri arttığında yapılacak her şeyi tek yerden tetikler.
 *
 * Serinin artabileceği iki yol var (gün kapanışı ve Ödüller'den satın
 * alma) ve §7.7 gereği ikisi arasında fark yok — o yüzden kutlama da
 * tek bir fonksiyondan geçiyor, iki yerde ayrı ayrı kurulmuyor.
 *
 * @param newStreak     Artıştan sonraki seri.
 * @param longestStreak Kayıtlı en uzun seri (rekor kontrolü için).
 */
export async function announceStreakIncrease(
  newStreak: number,
  longestStreak: number,
): Promise<void> {
  showStreakCelebration({
    streak: newStreak,
    // `longestStreak` artış sırasında zaten güncellendiği için rekor
    // kırıldığında ikisi EŞİT olur; ">" değil ">=" karşılaştırıyoruz.
    isRecord: newStreak > 0 && newStreak >= longestStreak,
  });

  await notifyStreakIncrease(newStreak);
}
